import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Spinner, Alert, Badge, Image, Pagination, Dropdown } from 'react-bootstrap';
import { IconSearch, IconFilter, IconEye, IconRefreshCw, IconChevronUp, IconChevronDown, IconTrendingUp, IconTrendingDown, IconHelpCircle, IconPencil, IconCheckSquare } from '../components/Icons';
import { fetchProducts, fetchFilterOptions, setFilters, clearFilters, setPagination } from '../store/slices/productsSlice';
import { Product } from '../store/slices/productsSlice';
import { fetchStatuses, changeProductStatus, changeProductStatusBatch } from '../store/slices/statusesSlice';
import { fetchSimilarProducts, clearSimilarProducts } from '../store/slices/similarProductsSlice';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import ProductTooltip from '../components/ProductTooltip';
import ImageTooltip from '../components/ImageTooltip';
import SimilarProductsModal from '../components/SimilarProductsModal';
import SubjectTransferModal from '../components/SubjectTransferModal';
import SubjectFilterSelect from '../components/SubjectFilterSelect';
import CountryFilterSelect from '../components/CountryFilterSelect';
import SourceFilterSelect from '../components/SourceFilterSelect';
import SourceBadge from '../components/SourceBadge';
import ProductInfoModal from '../components/ProductInfoModal';
import ProductHistoryModal from '../components/ProductHistoryModal';
import CheckerModal from '../components/CheckerModal';
import { loadCheckerPanelState, saveCheckerPanelState } from '../utils/checkerPanelStorage';
import CatalogChatMenu from '../components/Chat/CatalogChatMenu';
import { api } from '../services/api';
import { getWeekRangeMondayToToday } from '../utils/dateFormat';

const Products: React.FC = () => {
  const dispatch = useAppDispatch();
  const { products, loading, error, filters, pagination, filterOptions } = useAppSelector((state) => state.products);
  const { user } = useAppSelector((state) => state.auth);
  const { statuses, loading: statusesLoading } = useAppSelector((state) => state.statuses);
  const { similarProducts, loading: similarProductsLoading, error: similarProductsError } = useAppSelector((state) => state.similarProducts);
  const [changingStatus, setChangingStatus] = useState<string | null>(null);
  const [changingStatusBatch, setChangingStatusBatch] = useState<Set<string>>(new Set());
  
  // Состояния для drag and drop
  const [draggedProductId, setDraggedProductId] = useState<string | null>(null);
  const [draggedStatusId, setDraggedStatusId] = useState<string | null>(null);
  const [dragOverProductIds, setDragOverProductIds] = useState<Set<string>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  
  const [tooltip, setTooltip] = useState<{
    show: boolean;
    product: Product | null;
    position: { x: number; y: number };
  }>({
    show: false,
    product: null,
    position: { x: 0, y: 0 }
  });

  const [imageTooltip, setImageTooltip] = useState<{
    show: boolean;
    imageUrl: string;
    alt: string;
    position: { x: number; y: number };
  }>({
    show: false,
    imageUrl: '',
    alt: '',
    position: { x: 0, y: 0 }
  });

  const [searchInput, setSearchInput] = useState(filters.search);
  const [pageInput, setPageInput] = useState('');
  const [similarProductsModal, setSimilarProductsModal] = useState<{
    show: boolean;
    baseProduct: Product | null;
  }>({
    show: false,
    baseProduct: null
  });

  const [transferModal, setTransferModal] = useState<{
    show: boolean;
    subjectId: number | null;
    subjectName: string;
  }>({ show: false, subjectId: null, subjectName: '' });

  const [productInfoModal, setProductInfoModal] = useState<{
    show: boolean;
    productId: string;
    imageUrl: string;
  }>({ show: false, productId: '', imageUrl: '' });

  const [weekSummary, setWeekSummary] = useState<{ inWork: number; statusChanges: number; newProducts: number } | null>(null);
  const [historyModalProductId, setHistoryModalProductId] = useState<string | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [checkerModal, setCheckerModal] = useState<{ show: boolean; initialSku?: string }>(() => {
    const saved = loadCheckerPanelState();
    return { show: saved.open };
  });

  const tableRef = useRef<HTMLTableElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [headerWidths, setHeaderWidths] = useState<number[]>([]);

  // Функция для получения цвета статуса
  const getStatusColor = (statusName: string) => {
    const colorMap: { [key: string]: string } = {
      'В процессе': 'status-white',
      'Расчет ЮЭ': 'status-green',
      'Уже продаем': 'status-gray',
      'Слабый потенциал': 'status-red',
      'Не соответствует ассортиментной матрице': 'status-purple',
      'Сезонность': 'status-lightblue',
      'Доп вариация': 'status-teal',
      'Требуется дополнительная консультация': 'status-yellow',
      'Электротовары': 'status-red'
    };
    return colorMap[statusName] || 'status-white';
  };

  // Функция для получения цвета текущего статуса товара
  const getCurrentStatusColor = (product: Product) => {
    if (product.status_id && product.status_name) {
      return getStatusColor(product.status_name);
    }
    return 'status-white';
  };

  // Функция для получения цвета выбранного статуса в фильтре
  const getFilterStatusColor = () => {
    if (filters.status_id) {
      const selectedStatus = uniqueStatuses.find(status => status.id === filters.status_id);
      if (selectedStatus) {
        return getStatusColor(selectedStatus.name);
      }
    }
    return '';
  };

  useEffect(() => {
    dispatch(fetchStatuses());
    dispatch(fetchFilterOptions());
  }, [dispatch]);

  const loadWeekSummary = useCallback(() => {
    const { dateFrom, dateTo } = getWeekRangeMondayToToday();
    api.getWeeklyStatistics(dateFrom, dateTo).then((data) => {
      if (data.summary) {
        setWeekSummary({
          inWork: data.summary.inWork ?? 0,
          statusChanges: data.summary.statusChanges ?? 0,
          newProducts: data.summary.newProducts ?? 0
        });
      }
    }).catch(() => setWeekSummary(null));
  }, []);

  useEffect(() => {
    loadWeekSummary();
  }, [loadWeekSummary]);

  // Отладочный лог для filterOptions
  useEffect(() => {
    if (filterOptions) {
      console.log('filterOptions loaded:', filterOptions);
      console.log('subjects count:', filterOptions.subjects?.length);
      console.log('countries count:', filterOptions.countries?.length);
    }
  }, [filterOptions]);

  // Обновляем данные при изменении фильтров/пагинации
  useEffect(() => {
    dispatch(fetchProducts());
  }, [dispatch, filters, pagination.page, pagination.limit]);

  // Sticky headers logic
  useEffect(() => {
    const handleScroll = () => {
      if (!tableRef.current) return;

      const table = tableRef.current;
      const tableRect = table.getBoundingClientRect();
      const headerRow = table.querySelector('thead tr');
      
      if (!headerRow) return;

      // Check if table header should be sticky
      const shouldBeSticky = tableRect.top <= 0 && tableRect.bottom > 0;
      setIsSticky(shouldBeSticky);

      // Store header widths when first becoming sticky
      if (shouldBeSticky && headerWidths.length === 0) {
        const widths = Array.from(headerRow.children).map(th => 
          (th as HTMLElement).offsetWidth
        );
        setHeaderWidths(widths);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [headerWidths.length]);

  // Фильтрация и сортировка товаров
  // Данные приходят уже отфильтрованные и отсортированные на сервере
  const paginatedProducts = products;

  const handleFilterChange = (field: string, value: string) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    const timeoutId = setTimeout(() => {
      dispatch(setFilters({ search: value }));
    }, 300);
    return () => clearTimeout(timeoutId);
  };

  const SORT_FIELD_MAP: Record<string, string> = {
    id: 'product_id',
    name: 'product_name',
    subject: 'subject_name',
    average_price: 'price_final_average',
    revenue: 'stat_revenue',
    lost_profit: 'stat_lost_profit',
    first_date: 'meta_sku_first_date',
    country: 'meta_country',
  };
  const MAX_SORT_LEVELS = 3;

  const getSorts = () => {
    if (Array.isArray(filters.sorts) && filters.sorts.length > 0) return filters.sorts;
    if (filters.sort_field) {
      return [{ field: filters.sort_field, direction: (filters.sort_direction || 'ASC') as 'ASC' | 'DESC' }];
    }
    return [] as { field: string; direction: 'ASC' | 'DESC' }[];
  };

  const handleSort = (field: string) => {
    const backendField = SORT_FIELD_MAP[field] || field;
    const prev = getSorts();
    const idx = prev.findIndex((s) => s.field === backendField);
    let next: { field: string; direction: 'ASC' | 'DESC' }[];
    if (idx >= 0) {
      const current = prev[idx];
      // Cycle: DESC → ASC → off (remove column from stack)
      if (current.direction === 'DESC') {
        next = prev.map((s, i) => (i === idx ? { ...s, direction: 'ASC' as const } : s));
      } else {
        next = prev.filter((_, i) => i !== idx);
      }
    } else {
      next = [...prev, { field: backendField, direction: 'DESC' as 'ASC' | 'DESC' }];
      if (next.length > MAX_SORT_LEVELS) next = next.slice(next.length - MAX_SORT_LEVELS);
    }
    dispatch(setFilters({
      sorts: next,
      sort_field: next.map((s) => s.field).join(','),
      sort_direction: next.map((s) => s.direction).join(','),
    }));
  };

  const getSortIcon = (field: string) => {
    const backendField = SORT_FIELD_MAP[field] || field;
    const sorts = getSorts();
    const idx = sorts.findIndex((s) => s.field === backendField);
    if (idx < 0) {
      return <IconChevronUp className="sort-icon" />;
    }
    const Icon = sorts[idx].direction === 'ASC' ? IconChevronUp : IconChevronDown;
    return (
      <span className="sort-indicator active">
        <span className="sort-level">{idx + 1}</span>
        <Icon className="sort-icon active" />
      </span>
    );
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return '0 ₽';
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const formatSubjectName = (subjectName: string) => {
    if (!subjectName) return '-';
    const slashIndex = subjectName.indexOf('/');
    return slashIndex !== -1 ? subjectName.substring(slashIndex + 1) : subjectName;
  };

  const handlePageChange = (page: number) => {
    dispatch(setPagination({ page }));
  };

  const handlePageJump = () => {
    const pageNum = parseInt(pageInput);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      handlePageChange(pageNum);
      setPageInput('');
    } else {
      alert(`Введите номер страницы от 1 до ${totalPages}`);
    }
  };

  const handleStatusChange = async (productId: string, statusId: string) => {
    setChangingStatus(productId);
    try {
      await dispatch(changeProductStatus({ productId, statusId })).unwrap();
      setWeekSummary((prev) => prev ? { ...prev, statusChanges: prev.statusChanges + 1 } : prev);
      loadWeekSummary();
    } catch (error) {
      console.error('Failed to change product status:', error);
    } finally {
      setChangingStatus(null);
    }
  };

  // Batch изменение статусов
  const handleBatchStatusChange = async (productIds: string[], statusId: string) => {
    const newChangingBatch = new Set(productIds);
    setChangingStatusBatch(newChangingBatch);
    
    try {
      const result = await dispatch(changeProductStatusBatch({ productIds, statusId })).unwrap();
      const successCount = result.summary?.success ?? result.errors ? productIds.length - result.errors.length : productIds.length;
      if (successCount > 0) {
        setWeekSummary((prev) => prev ? { ...prev, statusChanges: prev.statusChanges + successCount } : prev);
        loadWeekSummary();
      }
      if (result.errors && result.errors.length > 0) {
        console.error('Failed to change status for some products:', result.errors);
      }
      if (result.summary) {
        console.log(`Batch status change completed: ${result.summary.success} successful, ${result.summary.failures} failed`);
      }
    } catch (error: any) {
      console.error('Failed to change product statuses:', error);
    } finally {
    setChangingStatusBatch(new Set());
    }
  };

  // Обработчики drag and drop
  const handleDragStart = (e: React.DragEvent, productId: string, statusId: string | null) => {
    const source = paginatedProducts.find(p => p.id === productId);
    if (!statusId || source?.tz_confirmed) {
      e.preventDefault();
      return;
    }
    setDraggedProductId(productId);
    setDraggedStatusId(statusId);
    setIsDragging(true);
    setDragOverProductIds(new Set([productId])); // Начинаем с исходной строки
    
    // Устанавливаем данные для перетаскивания
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', productId);
  };

  const handleDragOver = (e: React.DragEvent, targetProductId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedProductId || !draggedStatusId) return;
    
    // Находим индексы исходной и целевой строк
    const sourceIndex = paginatedProducts.findIndex(p => p.id === draggedProductId);
    const targetIndex = paginatedProducts.findIndex(p => p.id === targetProductId);
    
    if (sourceIndex === -1 || targetIndex === -1) return;
    
    // Определяем все ID товаров между исходной и целевой строкой (включительно)
    const startIndex = Math.min(sourceIndex, targetIndex);
    const endIndex = Math.max(sourceIndex, targetIndex);
    const affectedProductIds = paginatedProducts
      .slice(startIndex, endIndex + 1)
      .map(p => p.id);
    
    setDragOverProductIds(new Set(affectedProductIds));
  };

  const handleDrop = async (e: React.DragEvent, targetProductId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedProductId || !draggedStatusId) return;
    
    // Пропускаем товары с подтверждённым ТЗ — статус менять нельзя
    const affectedProductIds = Array.from(dragOverProductIds).filter((id) => {
      const product = paginatedProducts.find(p => p.id === id);
      return product && !product.tz_confirmed;
    });
    
    if (affectedProductIds.length > 0) {
      await handleBatchStatusChange(affectedProductIds, draggedStatusId);
    }
    
    // Очищаем состояния
    handleDragEnd();
  };

  const handleDragEnd = () => {
    setDraggedProductId(null);
    setDraggedStatusId(null);
    setDragOverProductIds(new Set());
    setIsDragging(false);
  };

  // Обработчики для tooltip
  const handleProductNameMouseEnter = (product: Product, event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      show: true,
      product,
      position: {
        x: rect.left + rect.width / 2,
        y: rect.top
      }
    });
  };

  const handleProductNameMouseLeave = () => {
    setTooltip({
      show: false,
      product: null,
      position: { x: 0, y: 0 }
    });
  };

  // Обработчики для увеличенного изображения
  const handleImageMouseEnter = (imageUrl: string, alt: string, event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setImageTooltip({
      show: true,
      imageUrl,
      alt,
      position: {
        x: rect.left + rect.width / 2,
        y: rect.top
      }
    });
  };

  const handleImageMouseLeave = () => {
    setImageTooltip({
      show: false,
      imageUrl: '',
      alt: '',
      position: { x: 0, y: 0 }
    });
  };

  // Обработчики для похожих товаров
  const handleSimilarProductsClick = async (product: Product, event: React.MouseEvent) => {
    event.stopPropagation();
    setSimilarProductsModal({
      show: true,
      baseProduct: product
    });
    dispatch(fetchSimilarProducts({ productId: product.id, limit: 20 }));
  };

  const handleSimilarProductsModalClose = () => {
    setSimilarProductsModal({
      show: false,
      baseProduct: null
    });
    dispatch(clearSimilarProducts());
  };

  const handleTransferClick = (e: React.MouseEvent, subjectId: number, subjectName: string) => {
    e.stopPropagation();
    setTransferModal({ show: true, subjectId, subjectName });
  };

  const handleTransferSuccess = () => {
    setTransferModal({ show: false, subjectId: null, subjectName: '' });
    dispatch(fetchProducts()); // Обновить список товаров
    loadWeekSummary();
  };

  // Обработчик клика на изображение для открытия модального окна с информацией
  const handleImageClick = (product: Product, e: React.MouseEvent) => {
    e.stopPropagation();
    if (product.thumb_url) {
      setProductInfoModal({
        show: true,
        productId: product.id,
        imageUrl: product.thumb_url
      });
    }
  };

  // Получаем уникальные значения для фильтров
  // Предметы и страны теперь берутся из БД через filterOptions
  // Статусы уже загружаются из БД через fetchStatuses
  const uniqueSubjects = useMemo(() => {
    // Всегда используем filterOptions, если они загружены
    if (filterOptions?.subjects && filterOptions.subjects.length > 0) {
      return filterOptions.subjects;
    }
    // Fallback на данные из products только если filterOptions еще не загружены
    const subjects = products.map(p => ({ id: p.subject_id, name: p.subject_name }));
    return subjects.filter((subject, index, self) => 
      index === self.findIndex(s => s.id === subject.id)
    );
  }, [filterOptions, products]);

  const uniqueStatuses = useMemo(() => {
    // Статусы уже загружаются из БД через fetchStatuses
    return statuses.map(status => ({ id: status.id, name: status.name }));
  }, [statuses]);

  const uniqueCountries = useMemo(() => {
    if (filterOptions?.countries) {
      return filterOptions.countries;
    }
    // Fallback на данные из products, если filterOptions еще не загружены
    return Array.from(new Set(products.map(p => p.country).filter(Boolean)));
  }, [filterOptions, products]);

  const sourceOptions = filterOptions?.sources || [];

  const totalPages = pagination.totalPages || 0;

  return (
    <Container fluid className="py-4">
      <h2 className="mb-4">Товары</h2>

      {/* Фильтры */}
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <IconFilter className="me-2" />
              Фильтры и поиск
            </h5>
            <div>
              <Button variant="outline-secondary" size="sm" onClick={() => dispatch(clearFilters())} className="me-2">
                <IconRefreshCw />
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Поиск</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Поиск по названию, SKU, предмету, аналитику..."
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Исключить фразы</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="фраза1, фраза2"
                  value={filters.exclude_phrase}
                  onChange={(e) => handleFilterChange('exclude_phrase', e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Предмет</Form.Label>
                <SubjectFilterSelect
                  subjects={uniqueSubjects}
                  value={filters.subject_id}
                  onChange={(id) => handleFilterChange('subject_id', id)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Статус</Form.Label>
                <Form.Select
                  value={filters.status_id}
                  onChange={(e) => handleFilterChange('status_id', e.target.value)}
                  className={getFilterStatusColor()}
                >
                  <option value="">Все статусы</option>
                  {uniqueStatuses.map(status => (
                    <option 
                      key={status.id} 
                      value={status.id}
                      className={getStatusColor(status.name)}
                    >
                      {status.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Страна</Form.Label>
                <CountryFilterSelect
                  countries={uniqueCountries}
                  value={filters.country}
                  onChange={(country) => handleFilterChange('country', country)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Источник</Form.Label>
                <SourceFilterSelect
                  sources={sourceOptions}
                  value={filters.source_code}
                  onChange={(code) => handleFilterChange('source_code', code)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Цена от</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="0"
                  value={filters.min_price}
                  onChange={(e) => handleFilterChange('min_price', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Цена до</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Любая"
                  value={filters.max_price}
                  onChange={(e) => handleFilterChange('max_price', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Выручка от</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="0"
                  value={filters.min_revenue}
                  onChange={(e) => handleFilterChange('min_revenue', e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Таблица товаров */}
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center flex-wrap gap-2">
              <h5 className="mb-0">
                <IconSearch className="me-2" />
                Результаты поиска
                {products.length > 0 && (
                  <Badge bg="primary" className="ms-2">
                    {products.length} товаров
                  </Badge>
                )}
              </h5>
              {weekSummary !== null && (
                <span className="text-muted small">
                  В работе: {weekSummary.inWork} · Кол-во проверенных товаров: {weekSummary.statusChanges} · Прошли ЮЭ: {weekSummary.newProducts}
                </span>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" size="sm" id="checker-dropdown">
                  <IconCheckSquare size={14} className="me-1" />
                  Чекер
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => { saveCheckerPanelState({ open: true }); setCheckerModal({ show: true }); }}>
                    Открыть тут
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => window.open('/products/checker', '_blank')}>
                    Открыть в новой вкладке
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => window.open('/products/checker', '_blank', 'noopener,noreferrer,width=900,height=700')}>
                    Открыть в новом окне
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              <Button variant="outline-primary" size="sm" onClick={() => dispatch(fetchProducts())} disabled={loading}>
                <IconRefreshCw className={loading ? 'fa-spin' : ''} />
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body style={{ paddingTop: isSticky ? '60px' : undefined }}>
          {error && !loading && (
            <Alert variant="warning" className="mb-3">
              {error}
            </Alert>
          )}
            <>
              {/* Sticky header clone */}
              {isSticky && headerWidths.length > 0 && (
                <div 
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: '#343a40',
                    borderBottom: '1px solid #495057',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <Table responsive hover variant="dark" className="table-dark" style={{ marginBottom: 0 }}>
                    <thead>
                      <tr>
                        <th style={{ width: headerWidths[0] || 'auto' }}>img</th>
                        <th 
                          onClick={() => handleSort('id')} 
                          className="sortable-header"
                          style={{ width: headerWidths[1] || 'auto' }}
                        >
                          ID (SKU)
                          {getSortIcon('id')}
                        </th>
                        <th 
                          onClick={() => handleSort('name')} 
                          className="sortable-header"
                          style={{ width: headerWidths[2] || 'auto' }}
                        >
                          Название
                          {getSortIcon('name')}
                        </th>
                        <th style={{ width: headerWidths[3] || '200px' }}>Статус товара</th>
                        <th
                          style={{ width: headerWidths[4] || 'auto' }}
                          onClick={() => handleSort('subject')}
                          className="sortable-header"
                        >
                          Предмет
                          {getSortIcon('subject')}
                        </th>
                        {/* <th style={{ width: headerWidths[5] || 'auto' }}>Аналитик</th> */}
                        <th
                          onClick={() => handleSort('average_price')}
                          className="sortable-header"
                          style={{ width: headerWidths[6] || 'auto' }}
                        >
                          Средняя цена
                          {getSortIcon('average_price')}
                        </th>
                        <th 
                          onClick={() => handleSort('revenue')} 
                          className="sortable-header"
                          style={{ width: headerWidths[7] || 'auto' }}
                        >
                          Выручка
                          {getSortIcon('revenue')}
                        </th>
                        <th
                          onClick={() => handleSort('lost_profit')}
                          className="sortable-header"
                          style={{ width: headerWidths[8] || 'auto' }}
                        >
                          Lost prof
                          {getSortIcon('lost_profit')}
                        </th>
                        <th 
                          onClick={() => handleSort('country')} 
                          className="sortable-header"
                          style={{ width: headerWidths[9] || 'auto' }}
                        >
                          Страна
                          {getSortIcon('country')}
                        </th>
                        <th
                          onClick={() => handleSort('first_date')}
                          className="sortable-header"
                          style={{ width: headerWidths[10] || 'auto' }}
                        >
                          Первая дата
                          {getSortIcon('first_date')}
                        </th>
                        <th style={{ width: headerWidths[11] || 'auto' }}>1 этап</th>
                        <th style={{ width: headerWidths[12] || 'auto' }}>2 этап</th>
                      </tr>
                    </thead>
                  </Table>
                </div>
              )}

              <div className="position-relative">
                {loading && (
                  <div
                    className="position-absolute top-0 end-0 m-2"
                    style={{ zIndex: 2 }}
                    aria-hidden={!loading}
                  >
                    <Spinner animation="border" size="sm" role="status">
                      <span className="visually-hidden">Загрузка...</span>
                    </Spinner>
                  </div>
                )}
              <Table
                ref={tableRef}
                responsive
                hover
                variant="dark"
                className="table-dark"
                style={{ opacity: loading ? 0.7 : 1, transition: 'opacity 0.15s' }}
              >
                <thead>
                  <tr>
                    <th>img</th>
                    <th onClick={() => handleSort('id')} className="sortable-header">
                      ID (SKU)
                      {getSortIcon('id')}
                    </th>
                    <th onClick={() => handleSort('name')} className="sortable-header">
                      Название
                      {getSortIcon('name')}
                    </th>
                    <th style={{width: '200px'}}>Статус товара</th>
                    <th onClick={() => handleSort('subject')} className="sortable-header">
                      Предмет
                      {getSortIcon('subject')}
                    </th>
                    {/* <th>Аналитик</th> */}
                    <th onClick={() => handleSort('average_price')} className="sortable-header">
                      Средняя цена
                      {getSortIcon('average_price')}
                    </th>
                    <th onClick={() => handleSort('revenue')} className="sortable-header">
                      Выручка
                      {getSortIcon('revenue')}
                    </th>
                    <th onClick={() => handleSort('lost_profit')} className="sortable-header">
                      Lost prof
                      {getSortIcon('lost_profit')}
                    </th>
                        <th onClick={() => handleSort('country')} className="sortable-header">
                          Страна
                          {getSortIcon('country')}
                        </th>
                        <th onClick={() => handleSort('first_date')} className="sortable-header">
                          Первая дата
                          {getSortIcon('first_date')}
                        </th>
                        <th>1 этап</th>
                        <th>2 этап</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedProducts.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="text-center py-4">
                        {loading ? (
                          <Spinner animation="border" role="status">
                            <span className="visually-hidden">Загрузка...</span>
                          </Spinner>
                        ) : (
                          <span className="text-muted">Товары не найдены. Попробуйте изменить параметры поиска.</span>
                        )}
                      </td>
                    </tr>
                  ) : (
                  paginatedProducts.map((product) => (
                    <tr 
                      key={product.id}
                      className={dragOverProductIds.has(product.id) ? 'drag-over-row' : ''}
                      onClick={() => {
                        if (checkerModal.show) {
                          setCheckerModal(prev => ({ ...prev, initialSku: product.id }));
                        }
                      }}
                      style={checkerModal.show ? { cursor: 'pointer' } : undefined}
                    >
                      <td>
                        {product.thumb_url ? (
                          <Image 
                            src={product.thumb_url} 
                            alt={product.name}
                            width={50}
                            height={50}
                            className="rounded product-image-hover"
                            style={{ objectFit: 'cover', cursor: 'pointer' }}
                            onMouseEnter={(e) => handleImageMouseEnter(product.thumb_url!, product.name, e)}
                            onMouseLeave={handleImageMouseLeave}
                            onClick={(e) => handleImageClick(product, e)}
                          />
                        ) : (
                          <div className="bg-light rounded d-flex align-items-center justify-content-center" style={{ width: 50, height: 50 }}>
                            <IconEye className="text-muted" />
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-1">
                          {product.source_code && product.source_code !== 'wb' ? (
                            <SourceBadge
                              code={product.source_code}
                              title={product.source_title}
                              color={product.source_color}
                            />
                          ) : null}
                          {product.url || !(product as any).source_code || (product as any).source_code === 'wb' ? (
                          <a 
                            href={product.url || `https://www.wildberries.ru/catalog/${product.id}/detail.aspx`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary text-decoration-none"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {product.id}
                          </a>
                          ) : (
                            <span>{product.id}</span>
                          )}
                          <Dropdown onClick={(e) => e.stopPropagation()}>
                            <Dropdown.Toggle variant="link" size="sm" className="p-0 text-muted" title="Открыть в чекере">
                              <IconCheckSquare size={12} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => { saveCheckerPanelState({ open: true }); setCheckerModal({ show: true, initialSku: product.id }); }}>
                                Открыть тут
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => window.open(`/products/checker?sku=${encodeURIComponent(product.id)}`, '_blank')}>
                                Открыть в новой вкладке
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => window.open(`/products/checker?sku=${encodeURIComponent(product.id)}`, '_blank', 'noopener,noreferrer,width=900,height=700')}>
                                Открыть в новом окне
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                          <CatalogChatMenu productId={product.id} />
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div 
                            className="fw-bold product-name-hover flex-grow-1" 
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={(e) => handleProductNameMouseEnter(product, e)}
                            onMouseLeave={handleProductNameMouseLeave}
                          >
                            {product.name}
                          </div>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="similar-products-btn"
                            onClick={(e) => handleSimilarProductsClick(product, e)}
                            title="Показать похожие товары"
                          >
                            <IconHelpCircle size={14} />
                          </Button>
                        </div>
                      </td>
                      <td
                        draggable={!product.tz_confirmed}
                        onDragStart={(e) => handleDragStart(e, product.id, product.status_id || null)}
                        onDragOver={(e) => handleDragOver(e, product.id)}
                        onDrop={(e) => handleDrop(e, product.id)}
                        onDragEnd={handleDragEnd}
                        style={{ 
                          cursor: product.tz_confirmed
                            ? 'not-allowed'
                            : (isDragging && draggedProductId === product.id ? 'grabbing' : (product.status_id ? 'grab' : 'default')),
                          userSelect: 'none'
                        }}
                        className={dragOverProductIds.has(product.id) ? 'drag-over-cell' : ''}
                        title={product.tz_confirmed ? 'Статус нельзя менять: ТЗ подтверждено' : undefined}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <Form.Select
                            size="sm"
                            value={product.status_id || ''}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleStatusChange(product.id, e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => {
                              // Разрешаем перетаскивание только если клик не на самом select
                              if ((e.target as HTMLElement).tagName === 'SELECT') {
                                e.stopPropagation();
                              }
                            }}
                            onDragStart={(e) => e.stopPropagation()}
                            disabled={!!product.tz_confirmed || statusesLoading || changingStatus === product.id || changingStatusBatch.has(product.id)}
                            title={product.tz_confirmed ? 'Статус нельзя менять: ТЗ подтверждено' : undefined}
                            className={getCurrentStatusColor(product)}
                          >
                            {statuses.map(status => (
                              <option 
                                key={status.id} 
                                value={status.id}
                                className={getStatusColor(status.name)}
                              >
                                {status.name}
                              </option>
                            ))}
                          </Form.Select>
                          {(changingStatus === product.id || changingStatusBatch.has(product.id)) && (
                            <Spinner size="sm" animation="border" variant="primary" />
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span>{formatSubjectName(product.subject_name)}</span>
                          {product.subject_id && (
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0"
                              style={{ minWidth: 'auto', padding: '0 !important' }}
                              onClick={(e) => handleTransferClick(e, product.subject_id!, product.subject_name || '')}
                              title="Передать предмет другому пользователю"
                            >
                              <IconPencil size={16} className="text-muted" />
                            </Button>
                          )}
                        </div>
                      </td>
                      {/* <td>
                        <div>{product.analyst_first_name} {product.analyst_last_name}</div>
                        <small className="text-muted">{product.analyst_email}</small>
                      </td> */}
                      <td>
                        <div className="fw-bold">{formatCurrency(product.average_price)}</div>
                        <small className="text-muted">Медиана: {formatCurrency(product.median_price)}</small>
                      </td>
                      <td>
                        <div className="fw-bold text-success">{formatCurrency(product.revenue)}</div>
                        <small className="text-muted">Потенциал: {formatCurrency(product.revenue_potential)}</small>
                      </td>
                      <td>
                        <div className="fw-bold text-danger">{formatCurrency(product.lost_profit)}</div>
                      </td>
                      <td>{product.country || '-'}</td>
                      <td>{formatDate(product.first_date)}</td>
                      <td>
                        <Badge bg="secondary">Не начат</Badge>
                      </td>
                      <td>
                        <Badge bg="secondary">Не начат</Badge>
                      </td>
                    </tr>
                  ))
                  )}
                </tbody>
              </Table>
              </div>

              <ProductHistoryModal
                show={showHistoryModal}
                onHide={() => { setShowHistoryModal(false); setHistoryModalProductId(null); }}
                productId={historyModalProductId || ''}
                productLabel={historyModalProductId ? `ID ${historyModalProductId}` : undefined}
              />

              {/* Пагинация */}
              <div className="d-flex justify-content-between align-items-center mt-3">
                {totalPages > 1 ? (
                  <div className="d-flex justify-content-center align-items-center">
                  <Pagination>
                    <Pagination.First 
                      onClick={() => handlePageChange(1)}
                      disabled={pagination.page === 1}
                    />
                    <Pagination.Prev 
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                    />
                    
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum = Math.max(1, Math.min(totalPages - 4, pagination.page - 2)) + i;
                      return (
                        <Pagination.Item
                          key={pageNum}
                          active={pageNum === pagination.page}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      );
                    })}
                    
                    <Pagination.Next 
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === totalPages}
                    />
                    <Pagination.Last 
                      onClick={() => handlePageChange(totalPages)}
                      disabled={pagination.page === totalPages}
                    />
                  </Pagination>
                    <div className="d-flex align-items-center gap-2 ms-3">
                      <Form.Control
                        type="number"
                        size="sm"
                        value={pageInput}
                        onChange={(e) => setPageInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handlePageJump();
                          }
                        }}
                        placeholder="Стр."
                        style={{ maxWidth: '80px', textAlign: 'center' }}
                        min="1"
                        max={totalPages}
                      />
                      <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                        из {totalPages}
                      </span>
                      <Button size="sm" variant="outline-secondary" onClick={handlePageJump}>
                        Перейти
                      </Button>
                </div>
                  </div>
                ) : (
                  <div></div>
              )}
                <Button variant="outline-primary" size="sm" onClick={() => dispatch(fetchProducts())} disabled={loading}>
                  <IconRefreshCw className={loading ? 'fa-spin' : ''} />
                </Button>
              </div>
            </>
        </Card.Body>
      </Card>

      {/* Product Tooltip */}
      <ProductTooltip 
        product={tooltip.product!}
        show={tooltip.show}
        position={tooltip.position}
      />

      {/* Image Tooltip */}
      <ImageTooltip 
        imageUrl={imageTooltip.imageUrl}
        alt={imageTooltip.alt}
        show={imageTooltip.show}
        position={imageTooltip.position}
      />

      {/* Similar Products Modal */}
      <SimilarProductsModal
        show={similarProductsModal.show}
        onHide={handleSimilarProductsModalClose}
        similarProducts={similarProducts}
        loading={similarProductsLoading}
        error={similarProductsError}
        baseProductName={similarProductsModal.baseProduct?.name || ''}
        baseProductId={similarProductsModal.baseProduct?.id || ''}
      />

      {/* Subject Transfer Modal */}
      <SubjectTransferModal
        show={transferModal.show}
        subjectId={transferModal.subjectId}
        subjectName={transferModal.subjectName}
        onHide={() => setTransferModal({ show: false, subjectId: null, subjectName: '' })}
        onSuccess={handleTransferSuccess}
      />

      {/* Product Info Modal */}
      <ProductInfoModal
        show={productInfoModal.show}
        productId={productInfoModal.productId}
        imageUrl={productInfoModal.imageUrl}
        onHide={() => setProductInfoModal({ show: false, productId: '', imageUrl: '' })}
      />

      {/* Checker Modal */}
      <CheckerModal
        show={checkerModal.show}
        onHide={() => { saveCheckerPanelState({ open: false }); setCheckerModal({ show: false }); }}
        initialSku={checkerModal.initialSku}
      />
    </Container>
  );
};

export default Products;
